import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer from 'src/components/StyledComponents/StyledGridContainer';
import { ProductionMapping } from 'src/models/Management.interface';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledGrid from 'src/components/StyledComponents/StyledDataGrid';
import { getAllProductionMappings } from 'src/services/productionMapping.service';
import { SortOrder, SortBy } from 'src/models/Sort.types';
import ProductionMappingActionMenuButton from 'src/components/ActionButtons/ProductionMappingActionMenu';
import { pagination } from 'src/utils/properties.utils';

const ProductionMappingGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const [offset, setOffset] = useState(pagination.DEFAULT_PAGE);
  const [limit, setLimit] = useState(pagination.DEFAULT_PAGE_SIZE);
  const [mappings, setMappings] = useState<ProductionMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [renderValue, setRenderValue] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [order, setOrder] = useState<SortOrder>(SortOrder.ASC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);

  const processedMappings = mappings.map((mapping, index) => ({
    id: mapping.id,
    number: (offset - 1) * limit + index + 1,
    name: mapping.productName,
    productId: mapping.productId,
    rawMaterialDetails: mapping.rawMaterialDetails,
  }));

  const fetchAndSetMappings = async () => {
    setLoading(true);
    try {
      const response = await getAllProductionMappings(
        offset,
        limit,
        searchTerm,
        sortBy,
        order
      );
      setMappings(response.data);
      setTotalItems(response.totalItems);
    } catch {
      setMappings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetMappings();
  }, [state, renderValue, sortBy, order, searchTerm, offset, limit]);

  useEffect(() => {
    setOffset(pagination.DEFAULT_PAGE);
  }, [searchTerm]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setOffset(pagination.DEFAULT_PAGE);
    setSortBy((sort?.field as SortBy) || 'name');
    setOrder((sort?.sort as SortOrder) || SortOrder.ASC);
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Product Name',
      flex: 1,
      sortable: true,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const mapping: ProductionMapping = {
          id: params.row.id,
          productId: params.row.productId,
          productName: params.row.name,
          rawMaterialDetails: params.row.rawMaterialDetails,
        };

        return (
          <ProductionMappingActionMenuButton
            mapping={mapping}
            onUpdate={() => setRenderValue((prev) => prev + 1)}
          />
        );
      },
    },
  ];

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={offset}
            setPage={setOffset}
            pageSize={limit}
            setPageSize={setLimit}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledGrid
        rows={processedMappings}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default ProductionMappingGrid;
